import Component from '@glimmer/component';
import { getDashComponent } from 'tio-employee/utils/wellness-insights';
import { Divider } from 'tio-ui/components/utilities';
import { VStack } from 'tio-ui/components/layout';
import { Section } from 'tio-ui/components/layout';
import WellnessTrainModel from 'tio-common/models/wellness-train';
import { t } from 'ember-intl';
import type StoreService from '@ember-data/store';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import RouterService from '@ember/routing/router-service';
import { Modal } from 'tio-ui/components/modal';
import type WellnessInsightsController from 'tio-employee/controllers/authenticated/wellness-insights';
import { Form, RadioGroup, Textarea } from 'tio-ui/components/forms';
import type { FormResultData } from 'tio-ui/components/forms';
import { ACCOUNT_DECLINED_REASON } from 'tio-common/models/answer-profile';
import type AnswerProfileModel from 'tio-common/models/answer-profile';
import { Button, ButtonGroup } from 'tio-ui/components/buttons';
import { on } from '@ember/modifier';
import type SessionContextService from 'tio-employee/services/session-context';
import { eq, not, notEq, and, or } from 'tio-ui/utilities';
import UploadInfoRec from 'tio-employee/components/observability/upload-info-rec';
import * as v from 'valibot';
import type WellnessTrainRecalculatorService from 'tio-common/services/wellness-train-recalculator';

const AnswerProfileSchema = v.pipe(
  v.object({
    accountDeclinedReason: v.nonNullable(v.string(), 'Reason not provided'),
    accountDeclinedComment: v.nullable(v.pipe(v.string(), v.nonEmpty())),
  }),
  v.forward(
    v.check((input) => {
      const reasonIsOther = input.accountDeclinedReason === ACCOUNT_DECLINED_REASON.other;
      const commentMissing = !input.accountDeclinedComment;
      return !(reasonIsOther && commentMissing);
    }, "Comment not provided with reason: 'Other'"),
    ['accountDeclinedComment']
  ),
  v.transform((input) => {
    const { accountDeclinedReason, accountDeclinedComment } = input;
    const reasonIsKnown = accountDeclinedReason !== ACCOUNT_DECLINED_REASON.other;
    const nullifiedComment = reasonIsKnown ? null : accountDeclinedComment;
    return { ...input, accountDeclinedComment: nullifiedComment };
  })
);

interface S {
  Args: {
    controller: WellnessInsightsController;
    model: WellnessTrainModel;
  };
}

type DeclineAccountFormData = {
  accountDeclinedReason?: string;
  accountDeclinedComment?: string | null;
};

export default class WellnessInsightsIndex extends Component<S> {
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare wellnessTrainRecalculator: WellnessTrainRecalculatorService;

  @tracked loading = false;

  get components() {
    return this.args.model?.componentDefinitions ?? [];
  }

  get hasScoreAndSummary() {
    return !!(this.args.model?.scoreDefinition && this.args.model?.summaryDefinition);
  }

  get accountCreationFeedbackActive() {
    return !!this.args.controller.accountCreationFeedbackActive;
  }

  get answerProfile() {
    return this.args.model?.user?.answerProfile;
  }

  get formProperties() {
    return this.answerProfile?.getProperties('accountDeclinedReason', 'accountDeclinedComment');
  }

  get canDeclineAccount() {
    return !!this.args.model?.accountPromptDefinition;
  }

  get answerProfileValid() {
    return !!this.formProperties && v.is(AnswerProfileSchema, this.formProperties);
  }

  resetAnswerProfile = () => {
    if (!this.args.model) {
      return;
    }
    const { user } = this.args.model;
    const { answerProfile } = user;

    if (answerProfile.isNew) {
      answerProfile.unloadRecord();
      user.answerProfile = this.store.createRecord<AnswerProfileModel>('answer-profile', {
        providingApp: 'tio-employee',
      });
    } else {
      answerProfile.rollbackAttributes();
    }
  };

  closeAccountCreationFeedback = () => {
    this.args.controller.set('accountCreationFeedbackActive', '');
    this.resetAnswerProfile();
  };

  onDeclineAccountFormChange = async (
    _result: FormResultData<DeclineAccountFormData>
  ): Promise<void> => {
    this.answerProfile?.setProperties(_result.data);
  };

  onDeclineAccountFormSubmit = async (
    _result: FormResultData<DeclineAccountFormData>,
    _event: SubmitEvent
  ): Promise<void> => {
    const result = v.safeParse(AnswerProfileSchema, this.formProperties);
    if (result.success) {
      this.answerProfile?.setProperties(result.output);
      this.loading = true;
      await this.answerProfile?.save();
      await this.wellnessTrainRecalculator.recalculate.perform();
      await this.args.model?.reload();
      this.args.controller.set('accountCreationFeedbackActive', '');
      this.loading = false;
    }
  };

  <template>
    {{#if @model}}
      <VStack>
        <h1 class="text-2xl font-semibold text-gray-900 mb-4">{{t
            "wellness_insights.student_loan_insights.title"
          }}</h1>
        {{#if this.hasScoreAndSummary}}
          <Section>
            <:header>{{t "common.status"}}</:header>
            <:body>
              <VStack>
                {{#if @model.scoreDefinition}}
                  {{#let (getDashComponent @model.scoreDefinition) as |ScoreComponent|}}
                    <ScoreComponent @componentDefinition={{@model.scoreDefinition}} />
                  {{/let}}
                {{/if}}
                {{#if @model.summaryDefinition}}
                  {{#let (getDashComponent @model.summaryDefinition) as |SummaryComponent|}}
                    <SummaryComponent @componentDefinition={{@model.summaryDefinition}} />
                  {{/let}}
                {{/if}}
                <UploadInfoRec />
              </VStack></:body>
          </Section>
        {{else if @model.accountPromptDefinition}}
          <Section>
            {{#let
              (getDashComponent @model.accountPromptDefinition)
              as |AccountCreationPromptComponent|
            }}
              <AccountCreationPromptComponent
                @componentDefinition={{@model.accountPromptDefinition}}
              />
            {{/let}}
          </Section>
        {{/if}}
        {{#if @model.recommendationDefinitions.length}}
          <Section>
            <:header>{{t "wellness_insights.dashboard.recommendations_title"}}</:header>
            <:body>
              <VStack @collapsed={{true}}>
                {{#each @model.recommendationDefinitions as |definition index|}}
                  {{#if (notEq index 0)}}
                    <Divider />
                  {{/if}}
                  {{#let (getDashComponent definition) as |DashComponent|}}
                    <DashComponent @componentDefinition={{definition}} />
                  {{/let}}
                {{/each}}
              </VStack>
            </:body>
          </Section>
        {{/if}}
      </VStack>
      <Modal
        @isOpen={{and this.accountCreationFeedbackActive this.canDeclineAccount}}
        @onClose={{this.closeAccountCreationFeedback}}
        as |m|
      >
        <m.Header>{{t "wellness_insights.dashboard.decline_account_modal.title"}}</m.Header>
        <m.Body class="flex flex-col gap-y-3">
          {{t "wellness_insights.dashboard.decline_account_modal.prompt"}}
          <Form
            @onChange={{this.onDeclineAccountFormChange}}
            @onSubmit={{this.onDeclineAccountFormSubmit}}
            class="flex flex-col gap-y-3"
          >
            <RadioGroup
              @name="accountDeclinedReason"
              @value={{this.answerProfile.accountDeclinedReason}}
              as |Radio|
            >
              <Radio
                @value={{ACCOUNT_DECLINED_REASON.doNotHave}}
                @label={{t
                  "wellness_insights.dashboard.decline_account_modal.form.reason.do_not_have"
                }}
              />
              <Radio
                @value={{ACCOUNT_DECLINED_REASON.doNotWant}}
                @label={{t
                  "wellness_insights.dashboard.decline_account_modal.form.reason.do_not_want"
                }}
              />
              <Radio
                @value={{ACCOUNT_DECLINED_REASON.other}}
                @label={{t "wellness_insights.dashboard.decline_account_modal.form.reason.other"}}
              />
            </RadioGroup>
            {{#if (eq this.answerProfile.accountDeclinedReason "other")}}
              <Textarea
                @name="accountDeclinedComment"
                @value={{this.answerProfile.accountDeclinedComment}}
                placeholder={{t
                  "wellness_insights.dashboard.decline_account_modal.form.comment.placeholder"
                }}
              />
            {{/if}}
            <ButtonGroup @class="flex justify-end gap-3">
              <Button
                @appearance="outlined"
                type="reset"
                {{on "click" this.closeAccountCreationFeedback}}
              >
                {{t "wellness_insights.dashboard.decline_account_modal.cancel"}}
              </Button>
              <Button type="submit" disabled={{or (not this.answerProfileValid) this.loading}}>
                {{t "wellness_insights.dashboard.decline_account_modal.submit"}}
              </Button>
            </ButtonGroup>
          </Form>
        </m.Body>
      </Modal>
    {{/if}}
  </template>
}
